import type { AnimalAvatar, AvatarItem, AvatarItemType } from '@/models/Avatar'

/**
 * Avatar configuration - Organized by unlock level
 * 
 * This file organizes all unlocks by level, making it easy to see what's
 * available at each progression point.
 * 
 * To add a new unlock:
 * 1. Add an entry to UNLOCKS_BY_LEVEL with the appropriate level
 * 2. Specify the type: 'animal', 'pose', or 'background'
 * 
 * The system will automatically generate all necessary IDs and paths.
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type UnlockType = 'animal' | 'pose' | 'background'

interface BaseUnlock {
  level: number
  type: UnlockType
}

interface AnimalUnlock extends BaseUnlock {
  type: 'animal'
  id: string
  name: string
  description: string
  baseImage: string
  imagePrefix?: string // Optional: custom prefix for image paths (defaults to animal id)
}

interface PoseUnlock extends BaseUnlock {
  type: 'pose'
  animalId: string
  name: string
  imageName: string // Filename part (e.g., 'happy', 'angry')
  category: 'emotion' | 'action' | 'special' | 'seasonal'
}

interface BackgroundUnlock extends BaseUnlock {
  type: 'background'
  name: string
  imageName: string // Filename part (e.g., 'forest', 'ocean')
}

type Unlock = AnimalUnlock | PoseUnlock | BackgroundUnlock

// ============================================================================
// UNLOCKS BY LEVEL
// ============================================================================

const UNLOCKS_BY_LEVEL: Unlock[] = [
  // Level 0
  {
    level: 0,
    type: 'animal',
    id: 'dog',
    name: 'Chien',
    description: 'Un chien loyal et énergique',
    baseImage: '/illustrations/animals/dog/dog-happy-optimized.webp',
  },
  {
    level: 0,
    type: 'animal',
    id: 'fox',
    name: 'Renard',
    description: 'Un renard intelligent et séduisant',
    baseImage: '/illustrations/animals/fox/fox-happy-optimized.webp',
  },
  {
    level: 0,
    type: 'pose',
    animalId: 'dog',
    name: 'Heureux',
    imageName: 'happy',
    category: 'emotion',
  },
  {
    level: 0,
    type: 'pose',
    animalId: 'fox',
    name: 'Heureux',
    imageName: 'happy',
    category: 'emotion',
  },
  {
    level: 0,
    type: 'background',
    name: 'Forêt',
    imageName: 'forest',
  },
  {
    level: 0,
    type: 'background',
    name: 'Océan',
    imageName: 'ocean',
  },

  // Level 1
  {
    level: 1,
    type: 'pose',
    animalId: 'dog',
    name: 'En colère',
    imageName: 'angry',
    category: 'emotion',
  },
  {
    level: 1,
    type: 'pose',
    animalId: 'fox',
    name: 'Bone',
    imageName: 'bone',
    category: 'action',
  },

  // Level 2
  {
    level: 2,
    type: 'pose',
    animalId: 'dog',
    name: 'En pleurs',
    imageName: 'cry',
    category: 'emotion',
  },
  {
    level: 2,
    type: 'pose',
    animalId: 'fox',
    name: 'En colère',
    imageName: 'angry',
    category: 'emotion',
  },
  
  // Level 3
  {
    level: 3,
    type: 'background',
    name: 'Chambre d\'enfant',
    imageName: 'kidbedroom',
  },
  
  {
    level: 3,
    type: 'pose',
    animalId: 'fox',
    name: 'En pleurs',
    imageName: 'cry',
    category: 'emotion',
  },
  {
    level: 3,
    type: 'background',
    name: 'Salle de séjour',
    imageName: 'livingroom',
  },

  // Level 4
  {
    level: 4,
    type: 'pose',
    animalId: 'dog',
    name: 'Doubt',
    imageName: 'doubting',
    category: 'emotion',
  },
  {
    level: 4,
    type: 'pose',
    animalId: 'fox',
    name: 'Champion',
    imageName: 'champion',
    category: 'action',
  },
  {
    level: 4,
    type: 'background',
    name: 'Désert',
    imageName: 'desert',
  },

  // Level 5
  {
    level: 5,
    type: 'animal',
    id: 'octopus',
    name: 'Pieuvre',
    description: 'Une pieuvre intelligente et mystérieuse',
    baseImage: '/illustrations/animals/octo/octo-cute-optimized.webp',
    imagePrefix: 'octo',
  },
  {
    level: 5,
    type: 'pose',
    animalId: 'octopus',
    name: 'Cute',
    imageName: 'cute',
    category: 'action',
  },
  {
    level: 5,
    type: 'background',
    name: 'Jungle',
    imageName: 'jungle',
  },

  // Level 6
  {
    level: 6,
    type: 'pose',
    animalId: 'dog',
    name: 'Success',
    imageName: 'success',
    category: 'emotion',
  },

  // Level 7
  {
    level: 7,
    type: 'pose',
    animalId: 'dog',
    name: 'Funny',
    imageName: 'funny',
    category: 'emotion',
  },
  {
    level: 7,
    type: 'pose',
    animalId: 'octopus',
    name: 'Angry',
    imageName: 'angry',
    category: 'action',
  },

  // Level 8
  {
    level: 8,
    type: 'pose',
    animalId: 'dog',
    name: 'Upset',
    imageName: 'upset',
    category: 'emotion',
  },

  // Level 9
  {
    level: 9,
    type: 'pose',
    animalId: 'dog',
    name: 'Dab',
    imageName: 'dab',
    category: 'emotion',
  },
  {
    level: 9,
    type: 'pose',
    animalId: 'octopus',
    name: 'Fiesta',
    imageName: 'fiesta',
    category: 'action',
  },

  // Level 10 - Jalon important : Nouvel animal
  {
    level: 10,
    type: 'animal',
    id: 'meerkat',
    name: 'Suricate',
    description: 'Un suricate intelligent et mystérieux',
    baseImage: '/illustrations/animals/meerkat/meerkat-cute-optimized.webp',
  },
  {
    level: 10,
    type: 'pose',
    animalId: 'meerkat',
    name: 'Cute',
    imageName: 'cute',
    category: 'action',
  },
  {
    level: 10,
    type: 'pose',
    animalId: 'fox',
    name: 'Rollers',
    imageName: 'rollers',
    category: 'action',
  },

  // Level 11
  {
    level: 11,
    type: 'pose',
    animalId: 'octopus',
    name: 'Laugh',
    imageName: 'laugh',
    category: 'action',
  },
  {
    level: 11,
    type: 'pose',
    animalId: 'dog',
    name: 'Guitar',
    imageName: 'guitar',
    category: 'special',
  },

  // Level 12
  {
    level: 12,
    type: 'pose',
    animalId: 'dog',
    name: 'Christmas',
    imageName: 'christmas',
    category: 'seasonal',
  },
  {
    level: 12,
    type: 'pose',
    animalId: 'meerkat',
    name: 'Sad',
    imageName: 'sad',
    category: 'action',
  },
  {
    level: 12,
    type: 'pose',
    animalId: 'fox',
    name: 'Trumpet',
    imageName: 'trumpet',
    category: 'special',
  },

  // Level 13
  {
    level: 13,
    type: 'pose',
    animalId: 'octopus',
    name: 'Sad',
    imageName: 'sad',
    category: 'action',
  },
  {
    level: 13,
    type: 'pose',
    animalId: 'dog',
    name: 'Skate',
    imageName: 'skate',
    category: 'action',
  },

  // Level 14
  {
    level: 14,
    type: 'pose',
    animalId: 'meerkat',
    name: 'Present',
    imageName: 'present',
    category: 'action',
  },
  {
    level: 14,
    type: 'pose',
    animalId: 'octopus',
    name: 'Dab',
    imageName: 'dab',
    category: 'action',
  },

  // Level 15
  {
    level: 15,
    type: 'pose',
    animalId: 'octopus',
    name: 'Cuddle',
    imageName: 'cuddle',
    category: 'action',
  },

  // Level 16
  {
    level: 16,
    type: 'pose',
    animalId: 'meerkat',
    name: 'Birthday',
    imageName: 'birthday',
    category: 'action',
  },
  {
    level: 16,
    type: 'pose',
    animalId: 'octopus',
    name: 'Battery',
    imageName: 'battery',
    category: 'special',
  },

  // Level 17
  {
    level: 17,
    type: 'pose',
    animalId: 'meerkat',
    name: 'Smile',
    imageName: 'smile',
    category: 'emotion',
  },

  // Level 18
  {
    level: 18,
    type: 'pose',
    animalId: 'meerkat',
    name: 'Dab',
    imageName: 'dab',
    category: 'action',
  },
  {
    level: 18,
    type: 'pose',
    animalId: 'octopus',
    name: 'Yoga',
    imageName: 'yoga',
    category: 'action',
  },

  // Level 19
  {
    level: 19,
    type: 'pose',
    animalId: 'fox',
    name: 'Christmas',
    imageName: 'christmas',
    category: 'seasonal',
  },

  // Level 20 - Jalon : Poses premium
  // Toutes les poses uniques ont été débloquées - niveau de transition

  // Level 21
  // Toutes les poses uniques ont été débloquées - niveau de transition

  // Level 22
  // Toutes les poses uniques ont été débloquées - niveau de transition

  // Level 23
  // Toutes les poses uniques ont été débloquées - niveau de transition

  // Level 24
  // Toutes les poses uniques ont été débloquées - niveau de transition

  // Level 25
  // Toutes les poses uniques ont été débloquées - niveau de transition

  // Level 26 - Niveau final : Toutes les poses uniques sont débloquées !
  // Félicitations pour avoir atteint le niveau maximum !
]

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get animal config by ID
 */
function getAnimalConfig(animalId: string): AnimalUnlock | undefined {
  return UNLOCKS_BY_LEVEL.find(
    (u): u is AnimalUnlock => u.type === 'animal' && u.id === animalId
  )
}

/**
 * Generate image path for an animal pose
 */
function getAnimalPoseImagePath(
  animalId: string,
  imageName: string
): string {
  const animalConfig = getAnimalConfig(animalId)
  const prefix = animalConfig?.imagePrefix || animalId
  return `/illustrations/animals/${prefix}/${prefix}-${imageName}-optimized.webp`
}

/**
 * Generate image path for a background
 */
function getBackgroundImagePath(imageName: string): string {
  return `/illustrations/bg/bg-all-${imageName}-optimized.webp`
}

/**
 * Generate ID for an avatar item
 */
function generateItemId(animalId: string, type: AvatarItemType, imageName: string): string {
  const prefix = type === 'background' ? 'bg-all' : `${animalId}-${type}`
  return `${prefix}-${imageName}`
}

// ============================================================================
// STARTER ANIMALS (only these can be assigned at registration/onboarding)
// ============================================================================

export const STARTER_ANIMAL_IDS = ['dog', 'fox'] as const

// ============================================================================
// GENERATED CONFIGURATIONS
// ============================================================================

/**
 * Generate AnimalAvatar array from configuration
 */
export const ANIMALS: AnimalAvatar[] = UNLOCKS_BY_LEVEL
  .filter((u): u is AnimalUnlock => u.type === 'animal')
  .map((animal) => ({
    id: animal.id,
    name: animal.name,
    description: animal.description,
    baseImage: animal.baseImage,
    unlockLevel: animal.level,
  }))

/**
 * Generate AvatarItem array from configuration
 */
export const AVATAR_ITEMS: AvatarItem[] = UNLOCKS_BY_LEVEL
  .filter((u): u is PoseUnlock | BackgroundUnlock => u.type === 'pose' || u.type === 'background')
  .map((unlock) => {
    if (unlock.type === 'pose') {
      return {
        id: generateItemId(unlock.animalId, 'pose', unlock.imageName),
        type: 'pose' as AvatarItemType,
        animalId: unlock.animalId,
        name: unlock.name,
        imagePath: getAnimalPoseImagePath(unlock.animalId, unlock.imageName),
        unlockLevel: unlock.level,
        category: unlock.category,
      }
    } else {
      return {
        id: generateItemId('all', 'background', unlock.imageName),
        type: 'background' as AvatarItemType,
        animalId: 'all',
        name: unlock.name,
        imagePath: getBackgroundImagePath(unlock.imageName),
        unlockLevel: unlock.level,
        category: 'scene',
      }
    }
  })

/**
 * Get all animals that the child has unlocked based on their level
 */
export function getUnlockedAnimals(childLevel: number): AnimalAvatar[] {
  return ANIMALS.filter((animal) => animal.unlockLevel <= childLevel)
}

/**
 * Get all avatar items that the child has unlocked based on their level
 * Can filter by animal ID and/or item type
 */
export function getUnlockedItems(
  childLevel: number,
  animalId?: string,
  itemType?: string
): AvatarItem[] {
  return AVATAR_ITEMS.filter((item) => {
    const levelMatch = item.unlockLevel <= childLevel
    const animalMatch = !animalId || item.animalId === animalId || item.animalId === 'all'
    const typeMatch = !itemType || item.type === itemType
    return levelMatch && animalMatch && typeMatch
  })
}

/**
 * Get a specific animal by ID
 */
export function getAnimalById(animalId: string): AnimalAvatar | undefined {
  return ANIMALS.find((animal) => animal.id === animalId)
}

/**
 * Get a specific avatar item by ID
 */
export function getAvatarItemById(itemId: string): AvatarItem | undefined {
  return AVATAR_ITEMS.find((item) => item.id === itemId)
}

/**
 * Check if a child has unlocked a specific animal
 */
export function isAnimalUnlocked(animalId: string, childLevel: number): boolean {
  const animal = getAnimalById(animalId)
  return animal ? animal.unlockLevel <= childLevel : false
}

/**
 * Check if a child has unlocked a specific avatar item
 */
export function isItemUnlocked(itemId: string, childLevel: number): boolean {
  const item = getAvatarItemById(itemId)
  return item ? item.unlockLevel <= childLevel : false
}
